import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, DfeInventoryItemDetail, DfeDefinitionDetail } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ArrowRightLeft, Trash2, Package } from 'lucide-react';
import { TransferItemDialog } from '../components/TransferItemDialog';
import { isSafeImageUri } from '../utils';

export function ItemDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<DfeInventoryItemDetail | null>(null);
  const [definition, setDefinition] = useState<DfeDefinitionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTransfer, setShowTransfer] = useState(false);

  useEffect(() => {
    if (!itemId) return;

    let cancelled = false;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const itemData = await api.getDfeInventoryItem(itemId!);
        if (cancelled) return;
        setItem(itemData);

        const defData = await api.getDfeDefinition(itemData.itemDefId);
        if (cancelled) return;
        setDefinition(defData);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load item');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const handleTransfer = useCallback(
    async (toUsername: string) => {
      if (!itemId) return;
      await api.transferDfeItem({ toUsername, itemId });
      navigate('/a/arche_dfe');
    },
    [itemId, navigate],
  );

  const handleBurn = useCallback(async () => {
    if (!itemId || !confirm('Are you sure you want to burn this item? This cannot be undone.'))
      return;
    try {
      await api.burnDfeItem(itemId);
      navigate('/a/arche_dfe');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to burn item');
    }
  }, [itemId, navigate]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-destructive">{error || 'Item not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/a/arche_dfe')}>
          Back to Inventory
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/a/arche_dfe')}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back
      </Button>

      <div className="mb-6">
        {isSafeImageUri(definition?.imageUri) ? (
          <img
            src={definition!.imageUri!}
            alt={definition!.name}
            className="w-full max-h-96 object-contain rounded-lg bg-muted"
          />
        ) : (
          <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
            <Package className="h-16 w-16 text-muted-foreground" />
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{definition?.name || 'Unknown Item'}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Instance #{item.instanceNumber}</p>
            </div>
            <Badge>{item.status}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {definition?.description && (
            <p className="text-sm text-muted-foreground">{definition.description}</p>
          )}

          {definition?.marketCategory && (
            <div className="text-sm">
              <span className="text-muted-foreground">Market Category: </span>
              <Badge variant="outline">{definition.marketCategory}</Badge>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Item ID</span>
              <p className="font-mono text-xs truncate">{item.itemId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Acquired</span>
              <p>{new Date(item.acquiredAt).toLocaleDateString()}</p>
            </div>
          </div>

          {Object.keys(item.metadata).length > 0 && (
            <div>
              <span className="text-sm text-muted-foreground">Metadata</span>
              <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-32">
                {JSON.stringify(item.metadata, null, 2)}
              </pre>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowTransfer(true)}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Transfer
            </Button>
            <Button variant="destructive" onClick={handleBurn}>
              <Trash2 className="h-4 w-4 mr-2" />
              Burn
            </Button>
          </div>
        </CardContent>
      </Card>

      <TransferItemDialog
        item={
          definition
            ? {
                itemDefId: item.itemDefId,
                name: definition.name,
                description: definition.description,
                fungibility: 'non_fungible',
                thumbnailUri: definition.thumbnailUri,
                imageUri: definition.imageUri,
                definitionMetadata: definition.metadata,
                category: '',
                position: '',
                balanceId: null,
                quantity: null,
                itemId: item.itemId,
                instanceNumber: item.instanceNumber,
                itemMetadata: item.metadata,
                acquiredAt: item.acquiredAt,
              }
            : null
        }
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        onConfirm={handleTransfer}
      />
    </div>
  );
}
